import React, { useState, useEffect } from 'react';
import { Formik, Form, FormikHelpers, useFormikContext } from 'formik';
import { Card, Button, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isUndefined } from 'lodash';

import { ResetForm, ServerError } from '@components/Helper';
import { FormGroupControl } from '@components/Base';
import OtpCodeInputGroup from '@components/OtpCodeInputGroup';
import { selectCurrentUser } from '@components/Auth';

import { selectMobileNumberPage } from './selector';
import { mobileNumberUpdate, otpCodeRequest, OtpCodeRequestPayload } from './action';
import { FormValues } from './type';
import { useValidationSchema } from './hook';

const GROUP_PROPS = { className: 'mb-3' };

const FormForm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { errors: serverErrors, isSuccess } = useSelector(selectMobileNumberPage);
  const { values, isSubmitting, errors } = useFormikContext<FormValues>();

  const handleOtpCodeRequest = () => {
    const { mobileNumber } = values;
    if (!mobileNumber) return;
    if (!isUndefined(errors.mobileNumber)) return;
    const payload: OtpCodeRequestPayload = { mobileNumber };
    dispatch(otpCodeRequest(payload));
  };

  return (
    <Form>
      <FormGroupControl
        type="tel"
        name="mobileNumber"
        label={t('user.mobileNumber')}
        groupProps={GROUP_PROPS}
      />
      <OtpCodeInputGroup onRequest={handleOtpCodeRequest} />
      <Button type="submit" disabled={isSubmitting}>
        {t('common:update')}
      </Button>
      <ServerError errors={serverErrors} />
      <ResetForm reset={isSuccess} />
    </Form>
  )
}

const MobileNumberPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const validationSchema = useValidationSchema();
  const currentUser = useSelector(selectCurrentUser);
  const { isSuccess } = useSelector(selectMobileNumberPage);
  const [shouldRenderAlert, setShouldRenderAlert] = useState(isSuccess);
  const initialValues: FormValues = {
    mobileNumber: '',
    otpCode: '',
  };

  const handleSubmit = (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    dispatch(mobileNumberUpdate(values));
    setSubmitting(false);
  };

  useEffect(() => {
    setShouldRenderAlert(isSuccess);
  }, [isSuccess]);

  return (
    <section>
      <h5 className="mb-4">{t('user.mobileNumber')}</h5>
      {shouldRenderAlert && (
        <Alert variant="success" onClose={() => setShouldRenderAlert(false)} dismissible>
          Your mobile number has been updated.
        </Alert>
      )}
      <Card>
        <Card.Body>
          {currentUser?.mobileNumber ? (
            <Card.Text>{currentUser.mobileNumber}</Card.Text>
          ) : (
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
              <FormForm />
            </Formik>
          )}
        </Card.Body>
      </Card>
    </section >
  );
};

export { MobileNumberPage };
